import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SEO } from "@/components/SEO";
import {
  CheckCircle2,
  Calendar,
  Shield,
  MapPin,
  FileText,
  AlertTriangle,
  XCircle,
  ArrowRight,
} from "lucide-react";

export default function SaskatchewanRoofingPermitsGuide() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "@id":
            "https://www.saskroofingcompany.com/blog/saskatchewan-roofing-permits-guide#article",
          headline:
            "Do You Need a Building Permit for a Roof Replacement in Saskatchewan? A City-by-City Guide",
          description:
            "Navigating roofing permits in Saskatchewan can be confusing. We break down the exact rules for Saskatoon, Regina, Moose Jaw, and more.",
          author: { "@id": "https://www.saskroofingcompany.com/#organization" },
          publisher: {
            "@id": "https://www.saskroofingcompany.com/#organization",
          },
          datePublished: "2026-06-01",
          inLanguage: "en-CA",
          isPartOf: {
            "@id": "https://www.saskroofingcompany.com/blog#webpage",
          },
          mainEntityOfPage:
            "https://www.saskroofingcompany.com/blog/saskatchewan-roofing-permits-guide",
          url: "https://www.saskroofingcompany.com/blog/saskatchewan-roofing-permits-guide",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.saskroofingcompany.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Blog",
              item: "https://www.saskroofingcompany.com/blog",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Saskatchewan Roofing Permits Guide",
              item: "https://www.saskroofingcompany.com/blog/saskatchewan-roofing-permits-guide",
            },
          ],
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Do I need a building permit for a roof replacement in Saskatoon?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "A standard shingle-over-plywood replacement typically requires no permit in Saskatoon. However, structural repairs to trusses or decking do require one.",
          },
        },
        {
          "@type": "Question",
          name: "Do I need a building permit for a roof replacement in Regina?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "In Regina, a permit is triggered by any structural work on the roof, including replacing rafters or trusses. Basic shingle replacements generally do not.",
          },
        },
        {
          "@type": "Question",
          name: "Do I need a building permit for a roof replacement in Moose Jaw?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Residential re-roofing does not require a permit in Moose Jaw if you are simply replacing shingles over existing sound decking.",
          },
        },
        {
          "@type": "Question",
          name: "Do I need a building permit for a roof replacement in Prince Albert?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Prince Albert follows standard rules: no permit for basic shingle replacements, but structural modifications require a permit from the city.",
          },
        },
        {
          "@type": "Question",
          name: "Do I need a building permit for a roof replacement in Swift Current?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Re-roofing in Swift Current does not need a permit, but any structural repairs to the roof deck or framing will require city approval.",
          },
        },
        {
          "@type": "Question",
          name: "Do I need a building permit for a roof replacement in Yorkton?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "In Yorkton, a standard roof replacement is exempt from permitting. However, structural changes or extensive decking replacement requires a permit.",
          },
        },
        {
          "@type": "Question",
          name: "Do I need a building permit for a roof replacement in Estevan?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Estevan allows basic re-roofing without a permit. If your roof requires structural repairs due to damage, a permit must be pulled.",
          },
        },
        {
          "@type": "Question",
          name: "Do I need a building permit for a roof replacement in Weyburn?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "You do not need a permit for a standard roof replacement in Weyburn, but structural alterations or repairs to the roof framing do require one.",
          },
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Saskatchewan Roofing Permits: City Guide | Sask Roofing"
        description="Navigating roofing permits in Saskatchewan can be confusing. We break down the exact rules for Saskatoon, Regina, Moose Jaw, and more."
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/ce4354b7-48f3-44eb-819c-602d7cecfa8c.png&output=webp&q=60"
            alt="City hall architecture in Saskatchewan"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10">
          <div className="max-w-4xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/80 text-primary-foreground text-sm font-medium mb-4">
              <Calendar className="w-4 h-4" /> Published June 1, 2026
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              Do You Need a Building Permit for a Roof Replacement in
              Saskatchewan?
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium">
              A complete city-by-city guide. Permits can be a hassle, but they
              are non-negotiable in some cases. Knowing the rules saves time,
              money, and rework.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-background">
        <div className="container px-4">
          <div className="max-w-4xl mx-auto space-y-12">
            {/* Intro */}
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p>
                When you're planning a{" "}
                <Link
                  to="/services/replacement"
                  className="text-primary font-medium hover:underline"
                >
                  roof replacement
                </Link>
                , the last thing you want to think about is bureaucratic red
                tape. Building permits are often viewed as a hassle, an extra
                expense, and a cause for delays. However, depending on the scope
                of your roofing project and the city you live in, a permit might
                be a strict legal requirement.
              </p>
              <p>
                Navigating the municipal bylaws of Saskatchewan's various cities
                can be confusing. To make it easier, we've compiled this
                comprehensive, city-by-city guide to roofing permits. We'll
                explain why permits matter, when you definitely need one, when
                you don't, and what the specific rules are for your local area.
              </p>
            </div>

            {/* TL;DR Box */}
            <div className="bg-primary/5 border-l-4 border-primary p-6 rounded-r-xl shadow-sm">
              <h2 className="flex items-center gap-2 text-xl font-bold text-foreground mb-3">
                <FileText className="w-6 h-6 text-primary" />
                The Short Answer
              </h2>
              <p className="text-lg text-muted-foreground mb-0">
                In almost all Saskatchewan cities, a standard
                "shingle-over-plywood" roof replacement does{" "}
                <strong>not</strong> require a building permit. However, if your
                project involves <strong>structural repairs</strong> (like
                replacing roof trusses, rafters, or extensive sections of
                decking), a permit is legally required.
              </p>
            </div>

            {/* Why Permits Matter */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">
                Why Permits Matter
              </h2>
              <p className="text-lg text-muted-foreground">
                It's tempting to skip the permit process to save a few dollars
                or get the job started faster, but the risks far outweigh the
                benefits. Building permits are a legal requirement under
                municipal building bylaws, designed to ensure that construction
                meets the National Building Code of Canada and local safety
                standards.
              </p>
              <div className="grid sm:grid-cols-2 gap-6 mt-6">
                <div className="bg-card border p-6 rounded-xl shadow-sm">
                  <h3 className="text-xl font-bold mb-3 flex items-center gap-2 text-destructive">
                    <AlertTriangle className="w-5 h-5" /> The Risks of Skipping
                  </h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Heavy municipal fines and stop-work orders.</li>
                    <li>• Forced tear-off of completed work for inspection.</li>
                    <li>
                      • Home insurance claim denials if unpermitted work fails.
                    </li>
                    <li>
                      • Complications when trying to sell your home later.
                    </li>
                  </ul>
                </div>
                <div className="bg-card border p-6 rounded-xl shadow-sm">
                  <h3 className="text-xl font-bold mb-3 flex items-center gap-2 text-green-600">
                    <Shield className="w-5 h-5" /> The Benefits of Compliance
                  </h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>
                      • Third-party verification that work is done safely.
                    </li>
                    <li>• Peace of mind that structural integrity is sound.</li>
                    <li>
                      • Smooth sailing during future real estate transactions.
                    </li>
                    <li>• Full protection under your homeowner's insurance.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* When You DO Need a Permit */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">
                When You DO Need a Permit
              </h2>
              <p className="text-lg text-muted-foreground">
                While basic shingle swaps are usually exempt, the following
                roofing scenarios almost always trigger the need for a building
                permit in Saskatchewan:
              </p>
              <ul className="space-y-4 text-muted-foreground">
                <li className="flex gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                  <span>
                    <strong>Structural Repairs:</strong> If we need to replace
                    or modify roof trusses, rafters, or significant portions of
                    the structural roof decking due to rot, snow load damage, or
                    tree impact.
                  </span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                  <span>
                    <strong>Multiple Layers:</strong> In some jurisdictions,
                    tearing off more than one existing layer of asphalt shingles
                    requires a permit and structural review.
                  </span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                  <span>
                    <strong>Commercial Roofing:</strong> Flat roofing and
                    commercial projects almost universally require permits and
                    engineering reviews.
                  </span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                  <span>
                    <strong>Roof Penetrations:</strong> Adding new skylights,
                    dormers, or significantly altering the roofline or
                    ventilation structures.
                  </span>
                </li>
              </ul>
            </div>

            {/* Inline Photo 1 */}
            <figure className="my-10 rounded-xl overflow-hidden shadow-md">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/58a25326-e7b1-4dd2-9b73-6eeb300a9973.png&output=webp&q=60"
                alt="A redacted residential building permit document on a clipboard"
                className="w-full h-auto object-cover aspect-[21/9]"
              />
              <figcaption className="bg-muted p-3 text-sm text-center text-muted-foreground">
                A building permit ensures that structural work is inspected and
                meets all local safety codes.
              </figcaption>
            </figure>

            {/* When You DON'T Need a Permit */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">
                When You DON'T Need a Permit
              </h2>
              <p className="text-lg text-muted-foreground">
                Fortunately, for the vast majority of our residential clients,
                the process is straightforward and permit-free. You typically do
                not need a permit for:
              </p>
              <div className="bg-muted/30 p-6 rounded-xl border">
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex gap-3 items-start">
                    <XCircle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                    <span>
                      <strong>Standard Re-roofing:</strong> Tearing off one
                      layer of old shingles and installing new underlayment and
                      shingles over existing, sound plywood decking.
                    </span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <XCircle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                    <span>
                      <strong>Minor Repairs:</strong> Replacing a few blown-off
                      shingles, fixing localized flashing issues, or sealing
                      minor leaks.
                    </span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <XCircle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                    <span>
                      <strong>Eavestroughs:</strong> Installing, repairing, or
                      replacing gutters and downspouts.
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* City-by-City Rules */}
            <div className="space-y-8 pt-6">
              <h2 className="text-3xl font-bold text-foreground">
                City-by-City Permit Rules
              </h2>
              <p className="text-lg text-muted-foreground">
                Here is a quick reference guide for the major cities we serve
                across Saskatchewan. Always verify with your local municipality
                if your project is complex.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {/* Saskatoon */}
                <div className="bg-card border p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />{" "}
                    <Link to="/areas/saskatoon" className="hover:underline">
                      Saskatoon
                    </Link>
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    A standard shingle-over-plywood replacement typically
                    requires no permit in Saskatoon. However, structural repairs
                    to trusses or decking do require one.
                  </p>
                </div>

                {/* Regina */}
                <div className="bg-card border p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />{" "}
                    <Link to="/areas/regina" className="hover:underline">
                      Regina
                    </Link>
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    In Regina, a permit is triggered by any structural work on
                    the roof, including replacing rafters or trusses. Basic
                    shingle replacements generally do not.
                  </p>
                </div>

                {/* Moose Jaw */}
                <div className="bg-card border p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />{" "}
                    <Link to="/areas/moose-jaw" className="hover:underline">
                      Moose Jaw
                    </Link>
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Residential re-roofing does not require a permit in Moose
                    Jaw if you are simply replacing shingles over existing sound
                    decking.
                  </p>
                </div>

                {/* Prince Albert */}
                <div className="bg-card border p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />{" "}
                    <Link to="/areas/prince-albert" className="hover:underline">
                      Prince Albert
                    </Link>
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Prince Albert follows standard rules: no permit for basic
                    shingle replacements, but structural modifications require a
                    permit from the city.
                  </p>
                </div>

                {/* Swift Current */}
                <div className="bg-card border p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />{" "}
                    <Link to="/areas/swift-current" className="hover:underline">
                      Swift Current
                    </Link>
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Re-roofing in Swift Current does not need a permit, but any
                    structural repairs to the roof deck or framing will require
                    city approval.
                  </p>
                </div>

                {/* Yorkton */}
                <div className="bg-card border p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />{" "}
                    <Link to="/areas/yorkton" className="hover:underline">
                      Yorkton
                    </Link>
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    In Yorkton, a standard roof replacement is exempt from
                    permitting. However, structural changes or extensive decking
                    replacement requires a permit.
                  </p>
                </div>

                {/* Estevan */}
                <div className="bg-card border p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />{" "}
                    <Link to="/areas/estevan" className="hover:underline">
                      Estevan
                    </Link>
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Estevan allows basic re-roofing without a permit. If your
                    roof requires structural repairs due to damage, a permit
                    must be pulled.
                  </p>
                </div>

                {/* Weyburn */}
                <div className="bg-card border p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />{" "}
                    <Link to="/areas/weyburn" className="hover:underline">
                      Weyburn
                    </Link>
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    You do not need a permit for a standard roof replacement in
                    Weyburn, but structural alterations or repairs to the roof
                    framing do require one.
                  </p>
                </div>
              </div>
            </div>

            {/* Common Permit Misconceptions */}
            <div className="space-y-6 pt-6">
              <h2 className="text-3xl font-bold text-foreground">
                Common Permit Misconceptions
              </h2>
              <p className="text-lg text-muted-foreground">
                There's a lot of bad advice out there regarding building
                permits. Let's clear up a few of the most common myths:
              </p>
              <div className="space-y-4">
                <div className="bg-muted/20 p-4 rounded-lg border-l-4 border-accent">
                  <h4 className="font-bold text-foreground">
                    Myth: "Permits are too expensive."
                  </h4>
                  <p className="text-muted-foreground text-sm mt-1">
                    Reality: A typical residential building permit for
                    structural roof repair in Saskatchewan costs between $50 and
                    $150. It's a negligible cost compared to the overall
                    project.
                  </p>
                </div>
                <div className="bg-muted/20 p-4 rounded-lg border-l-4 border-accent">
                  <h4 className="font-bold text-foreground">
                    Myth: "Getting a permit takes forever."
                  </h4>
                  <p className="text-muted-foreground text-sm mt-1">
                    Reality: For straightforward residential repairs, most city
                    permit offices in Saskatchewan can issue approval within 1
                    to 3 business days.
                  </p>
                </div>
                <div className="bg-muted/20 p-4 rounded-lg border-l-4 border-accent">
                  <h4 className="font-bold text-foreground">
                    Myth: "My contractor handles it, so I don't need to worry."
                  </h4>
                  <p className="text-muted-foreground text-sm mt-1">
                    Reality: While a good contractor will pull the permit for
                    you, the legal responsibility ultimately lies with the
                    homeowner. Always confirm in writing that they are handling
                    the permit and ask to see the approved document.
                  </p>
                </div>
              </div>
            </div>

            {/* Inline Photo 2 */}
            <figure className="my-10 rounded-xl overflow-hidden shadow-md">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a01b9c38-e76b-427a-bb96-09cbb15ad6ba.png&output=webp&q=60"
                alt="A beautiful completed residential asphalt shingle roof on a Saskatchewan home"
                className="w-full h-auto object-cover aspect-[21/9]"
              />
              <figcaption className="bg-muted p-3 text-sm text-center text-muted-foreground">
                A properly permitted and inspected roof gives you peace of mind
                that the structure is safe and secure.
              </figcaption>
            </figure>

            {/* How We Handle It */}
            <div className="bg-primary/5 border border-primary/20 p-8 rounded-xl my-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">
                How Sask Roofing Handles Permits
              </h2>
              <p className="text-muted-foreground mb-4">
                We believe in doing things by the book. If your project requires
                structural work, we take the headache out of the permit process
                for you:
              </p>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex gap-2 items-center">
                  <CheckCircle2 className="w-5 h-5 text-primary" /> We
                  coordinate all paperwork and applications with your local city
                  hall.
                </li>
                <li className="flex gap-2 items-center">
                  <CheckCircle2 className="w-5 h-5 text-primary" /> We schedule
                  the municipal inspections at the appropriate stages of
                  construction.
                </li>
                <li className="flex gap-2 items-center">
                  <CheckCircle2 className="w-5 h-5 text-primary" /> We document
                  the inspection pass with photos for your records.
                </li>
                <li className="flex gap-2 items-center">
                  <CheckCircle2 className="w-5 h-5 text-primary" /> We email you
                  the final permit confirmation and inspection report upon
                  completion.
                </li>
              </ul>
            </div>

            {/* FAQ Section */}
            <div className="space-y-6 pt-8 border-t">
              <h2 className="text-3xl font-bold text-foreground">
                City Permit FAQs
              </h2>
              <Accordion
                type="single"
                collapsible
                className="w-full bg-card border rounded-xl px-6 py-2 shadow-sm"
              >
                <AccordionItem value="item-1">
                  <AccordionTrigger className="text-left font-bold text-lg">
                    Do I need a building permit for a roof replacement in
                    Saskatoon?
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-base leading-relaxed data-[state=closed]:h-0"
                    forceMount={true}
                  >
                    A standard shingle-over-plywood replacement typically
                    requires no permit in Saskatoon. However, structural repairs
                    to trusses or decking do require one.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2">
                  <AccordionTrigger className="text-left font-bold text-lg">
                    Do I need a building permit for a roof replacement in
                    Regina?
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-base leading-relaxed data-[state=closed]:h-0"
                    forceMount={true}
                  >
                    In Regina, a permit is triggered by any structural work on
                    the roof, including replacing rafters or trusses. Basic
                    shingle replacements generally do not.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-3">
                  <AccordionTrigger className="text-left font-bold text-lg">
                    Do I need a building permit for a roof replacement in Moose
                    Jaw?
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-base leading-relaxed data-[state=closed]:h-0"
                    forceMount={true}
                  >
                    Residential re-roofing does not require a permit in Moose
                    Jaw if you are simply replacing shingles over existing sound
                    decking.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-4">
                  <AccordionTrigger className="text-left font-bold text-lg">
                    Do I need a building permit for a roof replacement in Prince
                    Albert?
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-base leading-relaxed data-[state=closed]:h-0"
                    forceMount={true}
                  >
                    Prince Albert follows standard rules: no permit for basic
                    shingle replacements, but structural modifications require a
                    permit from the city.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-5">
                  <AccordionTrigger className="text-left font-bold text-lg">
                    Do I need a building permit for a roof replacement in Swift
                    Current?
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-base leading-relaxed data-[state=closed]:h-0"
                    forceMount={true}
                  >
                    Re-roofing in Swift Current does not need a permit, but any
                    structural repairs to the roof deck or framing will require
                    city approval.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-6">
                  <AccordionTrigger className="text-left font-bold text-lg">
                    Do I need a building permit for a roof replacement in
                    Yorkton?
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-base leading-relaxed data-[state=closed]:h-0"
                    forceMount={true}
                  >
                    In Yorkton, a standard roof replacement is exempt from
                    permitting. However, structural changes or extensive decking
                    replacement requires a permit.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-7">
                  <AccordionTrigger className="text-left font-bold text-lg">
                    Do I need a building permit for a roof replacement in
                    Estevan?
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-base leading-relaxed data-[state=closed]:h-0"
                    forceMount={true}
                  >
                    Estevan allows basic re-roofing without a permit. If your
                    roof requires structural repairs due to damage, a permit
                    must be pulled.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-8">
                  <AccordionTrigger className="text-left font-bold text-lg">
                    Do I need a building permit for a roof replacement in
                    Weyburn?
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-base leading-relaxed data-[state=closed]:h-0"
                    forceMount={true}
                  >
                    You do not need a permit for a standard roof replacement in
                    Weyburn, but structural alterations or repairs to the roof
                    framing do require one.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/ce4354b7-48f3-44eb-819c-602d7cecfa8c.png&output=webp&q=60')] opacity-10 bg-cover bg-center mix-blend-overlay"></div>
        <div className="container px-4 relative z-10 text-center max-w-3xl mx-auto space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold">
            Not Sure If Your Roof Needs a Permit?
          </h2>
          <p className="text-xl text-primary-foreground/90">
            Book a free inspection. We'll assess your roof, tell you straight
            whether your project needs a permit, and handle the paperwork if it
            does.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center h-14 px-8 text-base font-bold bg-accent text-accent-foreground rounded-md hover:bg-accent/90 transition-colors"
            >
              Book a Free Inspection
            </Link>
            <a
              href="tel:6397397969"
              className="inline-flex items-center justify-center h-14 px-8 text-base font-bold bg-transparent border-2 border-primary-foreground text-primary-foreground rounded-md hover:bg-primary-foreground/10 transition-colors"
            >
              Call (639) 739-7969
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
